import { API_URL } from "../config/env";
import { smartFetch } from "../utils/http";

const PRIVATE_CDN_PREFIX = "https://static.puntt.ai/";

export type SignedPdfUrlResult =
  | { status: "ok"; url: string }
  | { status: "not_ready" }
  | { status: "error" };

function isPrivateCdnUrl(url: string): boolean {
  return (
    url.startsWith(PRIVATE_CDN_PREFIX) &&
    !url.startsWith(`${PRIVATE_CDN_PREFIX}public/`)
  );
}

/**
 * Exchange a private static.puntt.ai URL for a short-lived presigned S3 GET
 * URL that pdf.js can fetch without needing the service-worker auth injection.
 *
 * Returns a discriminated result so callers can distinguish "file still
 * processing" (404 on the signed-url endpoint) from genuine load failures.
 */
export async function getSignedPdfUrlResult(
  url: string,
): Promise<SignedPdfUrlResult> {
  if (!isPrivateCdnUrl(url)) {
    return { status: "ok", url };
  }
  try {
    const result = (await smartFetch(
      `${API_URL}/private/catalyst/files/signed-url?url=${encodeURIComponent(url)}`,
    )) as { url?: string } | null;
    if (result?.url) {
      return { status: "ok", url: result.url };
    }
    return { status: "error" };
  } catch (err) {
    if (err instanceof Response && err.status === 404) {
      return { status: "not_ready" };
    }
    console.error("[getSignedPdfUrl] failed to obtain presigned URL", err);
    return { status: "error" };
  }
}

/**
 * Legacy helper — returns the signed URL string on success, or the original
 * URL when signing fails. Prefer getSignedPdfUrlResult when the caller needs
 * to distinguish not-ready files from other errors.
 */
export async function getSignedPdfUrl(url: string): Promise<string> {
  const result = await getSignedPdfUrlResult(url);
  if (result.status === "ok") {
    return result.url;
  }
  return url;
}
